import { useCallback, useRef } from "react";

interface onIntersectProps extends IntersectionObserverInit {
  onIntersect: () => void;
  onDiverge?: () => void;
}

export function useOnIntersection({
  threshold = 0,
  root = null,
  rootMargin = "0%",
  onIntersect,
  onDiverge,
}: onIntersectProps): (node: any) => void {
  const hasIntersected = useRef(false);
  const unsubscribeObservers = useRef<() => void>(null);

  const intersectionHandler = useCallback(
    ([entry]: IntersectionObserverEntry[]): void => {
      const meetsThreshold = typeof threshold !== "number" || entry.intersectionRatio >= threshold;

      if (entry.isIntersecting && meetsThreshold) {
        hasIntersected.current = true;
        onIntersect();
      } else if (!entry.isIntersecting && hasIntersected.current) {
        onDiverge?.();
      }
    },
    [onDiverge, onIntersect, threshold],
  );

  const ref = useCallback(
    (node: any) => {
      if (!node) {
        unsubscribeObservers.current?.();
        unsubscribeObservers.current = null;

        return;
      }

      const hasIOSupport = !!window.IntersectionObserver;

      if (!hasIOSupport) {
        return;
      }

      unsubscribeObservers.current?.();

      const observerParams = { threshold, root, rootMargin };
      const observer = new IntersectionObserver(intersectionHandler, observerParams);

      observer.observe(node);
      unsubscribeObservers.current = () => observer.disconnect();
    },
    [threshold, root, rootMargin, intersectionHandler],
  );

  return ref;
}
